<template>
  <div class="w-full max-w-sm">

    <!-- Header -->
    <div class="text-center mb-8">
      <img src="/salestial-logo.png" alt="Salestial" class="w-14 h-14 mx-auto mb-4 drop-shadow" />
      <h1 class="text-3xl font-extrabold text-gray-900 mt-1">Salestial Dashboard</h1>
      <p class="text-gray-600 text-sm font-medium mt-2">Masuk untuk melihat insight pelanggan</p>
    </div>

    <!-- SSO error banner — dari query string ?sso_error=... setelah Google callback -->
    <p v-if="ssoErrorMessage" class="text-xs text-red-700 bg-red-50/90 px-3 py-2.5 rounded-lg border border-red-200 mb-4 leading-relaxed">
      <span class="block font-semibold mb-0.5">Login Google gagal</span>
      {{ ssoErrorMessage }}
    </p>

    <!-- Google SSO -->
    <a
      href="/auth/google"
      class="w-full inline-flex items-center justify-center gap-2.5 px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 text-gray-700 font-semibold text-sm transition-colors"
    >
      <svg class="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      Masuk dengan Google
    </a>

    <div class="my-5 flex items-center gap-3">
      <div class="flex-1 border-t border-gray-400/50"/>
      <span class="text-[10px] text-gray-600 uppercase tracking-widest font-semibold">atau pakai password</span>
      <div class="flex-1 border-t border-gray-400/50"/>
    </div>

    <!-- Form -->
    <form @submit.prevent="handleLogin" class="space-y-4">
      <div>
        <label class="block text-xs font-semibold text-gray-700 mb-1.5">Email</label>
        <input
          v-model="form.email"
          type="text"
          inputmode="email"
          autocomplete="username"
          autocapitalize="none"
          autocorrect="off"
          spellcheck="false"
          required
          placeholder="name@company.com"
          class="w-full px-3.5 py-2.5 border border-white/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent bg-white/60 text-gray-800 placeholder-gray-400"
        />
      </div>

      <div>
        <label class="block text-xs font-semibold text-gray-700 mb-1.5">Password</label>
        <input
          v-model="form.password"
          type="password"
          required
          placeholder="••••••••"
          class="w-full px-3.5 py-2.5 border border-white/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent bg-white/60 text-gray-800 placeholder-gray-400"
        />
      </div>

      <p v-if="error" class="text-xs text-red-600 bg-red-50/80 px-3 py-2 rounded-lg border border-red-100">
        {{ error }}
      </p>

      <button
        type="submit"
        :disabled="loading"
        class="w-full bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg transition-colors text-sm shadow-md shadow-sky-600/30 mt-2"
      >
        {{ loading ? 'Sedang masuk…' : 'Masuk' }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: 'auth', middleware: 'guest' })

const form = reactive({ email: '', password: '' })
const loading = ref(false)
const error = ref('')

const route = useRoute()
const ssoErrorMessage = computed(() => {
  const code = String(route.query.sso_error ?? '')
  if (!code) return ''
  const detail = String(route.query.detail ?? '')
  const trail = ' Hubungi Administrator Tim IT bila Anda merasa ini keliru.'
  switch (code) {
    case 'user_not_found':
      return `Email ${detail || 'Anda'} belum terdaftar di Salestial Dashboard.${trail}`
    case 'no_role':
      return `Akun ${detail || 'Anda'} ditemukan tapi belum memiliki role apapun.${trail}`
    case 'inactive':
      return `Akun ${detail || 'Anda'} sudah dinonaktifkan.${trail}`
    case 'email_unverified':
      return `Email Google ${detail || ''} belum terverifikasi. Verifikasi dulu di akun Google Anda, lalu coba lagi.`
    case 'state_mismatch':
      return 'Sesi login tidak valid (state mismatch). Coba lagi dari awal.'
    case 'canceled':
      return 'Login Google dibatalkan.'
    case 'missing_code':
    case 'no_access_token':
    case 'no_email':
      return `Login Google gagal di tahap otorisasi (${code}). Coba lagi atau hubungi Administrator Tim IT.`
    case 'token_exchange_failed':
    case 'userinfo_failed':
    case 'config_missing':
      return `Login Google gagal di server (${code}${detail ? ': ' + detail : ''}).${trail}`
    default:
      return `Login Google gagal: ${code}${detail ? ' (' + detail + ')' : ''}.${trail}`
  }
})

async function handleLogin() {
  loading.value = true
  error.value = ''

  try {
    await $fetch('/api/auth/login', {
      method: 'POST',
      body: form,
    })
    // external: true → muat ulang penuh, supaya SSR jalan dengan cookie sesi
    // yang baru. Tujuannya dari ?redirect=, fallback /dashboard (bukan '/').
    await navigateTo(tujuanLogin(route.query.redirect), { replace: true, external: true })
  } catch (err: any) {
    error.value = err?.data?.message ?? 'Login failed, please try again.'
  } finally {
    loading.value = false
  }
}
</script>
